/**
 * Rechnen in Berliner Kalendertagen (Europe/Berlin). Gebraucht, wenn ein Basar wiederholt wird: Alle Zeiten
 * wandern um ganze Kalendertage und behalten ihre Berliner Uhrzeit. Eine Verschiebung um Millisekunden
 * (z. B. 7 × 24 Stunden) würde bei der Zeitumstellung aus 10:00 Uhr 11:00 oder 09:00 Uhr machen.
 */

const FORMAT = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

export interface BerlinParts {
  year: number;
  /** 1 bis 12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

/** Wanduhr-Angaben des Zeitpunkts in Berlin. */
export function berlinParts(date: Date): BerlinParts {
  const values: Record<string, number> = {};
  for (const part of FORMAT.formatToParts(date)) {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  }
  return {
    year: values['year'] ?? 0,
    month: values['month'] ?? 0,
    day: values['day'] ?? 0,
    hour: values['hour'] ?? 0,
    minute: values['minute'] ?? 0,
    second: values['second'] ?? 0,
    millisecond: date.getUTCMilliseconds(),
  };
}

/** Berliner Kalendertag als „JJJJ-MM-TT“. */
export function berlinDay(date: Date): string {
  const { year, month, day } = berlinParts(date);
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Ganze Berliner Kalendertage zwischen zwei Zeitpunkten (`to` minus `from`, nach dem Kalenderdatum, nicht nach Stunden). */
export function berlinDaysBetween(from: Date, to: Date): number {
  const a = berlinParts(from);
  const b = berlinParts(to);
  const utcDay = (p: BerlinParts): number => Date.UTC(p.year, p.month - 1, p.day);
  return Math.round((utcDay(b) - utcDay(a)) / 86_400_000);
}

/**
 * Der Zeitpunkt zu einer Berliner Wanduhr-Angabe. Ein Tag über dem Monatsende läuft in den nächsten Monat über
 * (`day` = 32 im Januar ist der 1. Februar), so kann man ganze Kalendertage addieren.
 * Gibt es die Uhrzeit am Tag nicht (Stunde, die bei der Umstellung im Frühjahr ausfällt), rutscht sie auf die
 * nächste vorhandene Stunde. Gibt es sie doppelt (Herbst), gilt die erste.
 */
export function berlinInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second = 0,
  millisecond = 0,
): Date {
  const wall = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const target = new Date(wall);
  const want = {
    year: target.getUTCFullYear(),
    month: target.getUTCMonth() + 1,
    day: target.getUTCDate(),
    hour,
    minute,
  };
  // Berlin liegt im Sommer 2, im Winter 1 Stunde vor UTC
  for (const offsetHours of [2, 1]) {
    const candidate = new Date(wall - offsetHours * 3_600_000);
    const got = berlinParts(candidate);
    if (
      got.year === want.year &&
      got.month === want.month &&
      got.day === want.day &&
      got.hour === want.hour &&
      got.minute === want.minute
    ) {
      return candidate;
    }
  }
  return new Date(wall - 3_600_000);
}

/**
 * Verschiebt den Zeitpunkt um ganze Berliner Kalendertage und behält die Berliner Uhrzeit.
 * Gibt es die Uhrzeit am Zieltag nicht (Stunde, die bei der Umstellung im Frühjahr ausfällt), rutscht sie auf die
 * nächste vorhandene Stunde. Gibt es sie doppelt (Herbst), gilt die erste.
 */
export function shiftBerlinDays(date: Date, days: number): Date {
  const p = berlinParts(date);
  return berlinInstant(p.year, p.month, p.day + days, p.hour, p.minute, p.second, p.millisecond);
}
